import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import {
    fetchAdminApps,
    fetchAppRoles,
    fetchRoleMaps,
    fetchAdminAudit,
    fetchAppAccounts,
    type AdminRegistryApp,
    type AppAccountRow,
    type ProvisioningOutcome,
} from '@/api/console';
import type { AppDetailTabKey, AppRoleConversion } from './types';

// EAA-1888: default page size for the "Tài khoản" tab's real server
// pagination — matches the previous `clientPageSize={8}` this replaces, so
// the visible page shape is unchanged even though the data now genuinely
// arrives one page at a time.
const ACCOUNTS_DEFAULT_PAGE_SIZE = 8;

/** EAA-1888: re-exported under its pre-1888 name so `AppDetailAccountsTab.tsx`
 *  (and anything else importing this type) needs no rename — the row shape
 *  itself now comes straight off the wire (`AppAccountRow`, `@/api/console`)
 *  instead of being assembled client-side from a per-org fetch. */
export type AppDetailAccountRow = AppAccountRow;

const TAB_KEYS: AppDetailTabKey[] = [
    'overview',
    'roles',
    'accounts',
    'organizations',
    'connection',
    'audit',
];

/**
 * EAA-1817 (frames 47-51): data + tab-state for `/console/apps/:code`. Follows
 * the same shape `useAccountProfilePage` established for `/console/accounts/:sub`
 * — tab in `?tab=` so deep links and reload survive, detail resolved by
 * finding the row in the registry list rather than a dedicated single-row
 * fetch (the registry is small; `AccountProfilePage` needed its own fetch
 * because accounts are unbounded, apps are not).
 */
export function useAppDetailPage() {
    const { code = '' } = useParams();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const can = useCan();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const canView = can('console.apps.view');
    const canManage = can('console.apps.manage');
    const canViewAudit = can('console.audit.view');
    // EAA-1817: mirrors `AppsPage` — the catalog-sync trigger calls the exact
    // same `POST /role-map/sync` endpoint `/console/role-map`'s sync button
    // does, which the server gates under `console.rolemap.sync`, not
    // `console.apps.*`.
    const canSync = can('console.rolemap.sync');
    // EAA-1861: bulk-minting Keycloak identities is a distinct authority from
    // both `console.apps.manage` and `console.rolemap.sync` — see
    // permissions_catalog.py's own comment on `APPS_SYNC_USERS`.
    const canSyncUsers = can('console.apps.sync_users');
    // EAA-1878: bulk-mutating the org tree (create/update/deactivate
    // `Organization` rows) is gated separately from `console.apps.sync_users`
    // — see `permissions_catalog.py`'s comment on `APPS_SYNC_ORGANIZATIONS`.
    const canSyncOrganizations = can('console.apps.sync_organizations');

    const rawTab = searchParams.get('tab');
    const tab: AppDetailTabKey = (TAB_KEYS as string[]).includes(rawTab ?? '')
        ? (rawTab as AppDetailTabKey)
        : 'overview';

    const setTab = useCallback(
        (next: AppDetailTabKey) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                if (next === 'overview') params.delete('tab');
                else params.set('tab', next);
                return params;
            });
        },
        [setSearchParams]
    );

    const enabled = authenticated && Boolean(accessToken) && canView;

    const appsQuery = useQuery({
        queryKey: ['admin-apps', accessToken],
        queryFn: () => fetchAdminApps(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });

    const app: AdminRegistryApp | undefined = useMemo(
        () => appsQuery.data?.find((row) => row.code === code),
        [appsQuery.data, code]
    );

    const appRolesQuery = useQuery({
        queryKey: ['admin-app-roles', code, accessToken],
        queryFn: () =>
            fetchAppRoles(config.apiBase, accessToken as string, code, { includeInactive: true }),
        enabled: enabled && Boolean(code),
        retry: false,
    });

    // EAA-1817: default-scope role-maps only (organization_id === null) — the
    // same filter `RoleMapService.coverage()` applies server-side, so "converted"
    // here means the same thing the coverage board means by it.
    const roleMapsQuery = useQuery({
        queryKey: ['role-maps', accessToken],
        queryFn: () => fetchRoleMaps(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });

    const roleConversions: AppRoleConversion[] = useMemo(() => {
        const roles = appRolesQuery.data ?? [];
        const maps = (roleMapsQuery.data ?? []).filter(
            (row) => row.application === code && row.organization_id === null && row.is_active
        );
        return roles.map((role) => ({
            id: role.id,
            key: role.key,
            name: role.name,
            permissionCount: role.permissions.length,
            permissions: role.permissions,
            isActive: role.is_active,
            isSynced: role.last_synced_at !== null,
            convertedTo: maps.filter((row) => row.app_role === role.key).map((row) => row.role_key),
        }));
    }, [appRolesQuery.data, roleMapsQuery.data, code]);

    const convertedCount = useMemo(
        () => roleConversions.filter((r) => r.isActive && r.convertedTo.length > 0).length,
        [roleConversions]
    );
    const unconvertedCount = useMemo(
        () => roleConversions.filter((r) => r.isActive && r.convertedTo.length === 0).length,
        [roleConversions]
    );

    // EAA-1888: real server pagination/filter/search for the "Tài khoản" tab —
    // replaces the EAA-1840 design's own per-organization fan-out (fetch EVERY
    // org's full `provisioning-outcomes` list, merge client-side, then slice
    // 8 rows at a time in the browser). `AssignmentService.outcomes_for_app`
    // now applies the exact same relevance rule (direct grant to this app, OR
    // a role_key this app's default-scope RoleMap converts from, OR the
    // EAA-1861 "unassigned" placeholder) server-side — see its docstring —
    // so this hook has nothing left to compute beyond the query params
    // themselves. Any filter/search change resets to page 1, same convention
    // `useRealmAccountsAdapter.patchFilters` uses, so a narrowed result set
    // never leaves the operator stranded on a now-out-of-range page.
    const [accountsPage, setAccountsPageState] = useState(1);
    const [accountsPageSize, setAccountsPageSizeState] = useState(ACCOUNTS_DEFAULT_PAGE_SIZE);
    const [accountsSearch, setAccountsSearchState] = useState('');
    const [accountsOutcomeFilter, setAccountsOutcomeFilterState] = useState<ProvisioningOutcome | null>(null);
    const [accountsOrgFilter, setAccountsOrgFilterState] = useState<string | null>(null);
    const [accountsRoleFilter, setAccountsRoleFilterState] = useState<string | null>(null);
    const [accountsOrdering, setAccountsOrdering] = useState('');

    const setAccountsPage = useCallback((page: number) => setAccountsPageState(page), []);
    const setAccountsPageSize = useCallback((size: number) => {
        setAccountsPageSizeState(size);
        setAccountsPageState(1);
    }, []);
    const setAccountsSearch = useCallback((value: string) => {
        setAccountsSearchState(value);
        setAccountsPageState(1);
    }, []);
    const setAccountsOutcomeFilter = useCallback((value: ProvisioningOutcome | null) => {
        setAccountsOutcomeFilterState(value);
        setAccountsPageState(1);
    }, []);
    const setAccountsOrgFilter = useCallback((value: string | null) => {
        setAccountsOrgFilterState(value);
        setAccountsPageState(1);
    }, []);
    const setAccountsRoleFilter = useCallback((value: string | null) => {
        setAccountsRoleFilterState(value);
        setAccountsPageState(1);
    }, []);

    const accountsQuery = useQuery({
        queryKey: [
            'app-accounts',
            code,
            accessToken,
            accountsPage,
            accountsPageSize,
            accountsSearch,
            accountsOutcomeFilter,
            accountsOrgFilter,
            accountsRoleFilter,
            accountsOrdering,
        ],
        queryFn: () =>
            fetchAppAccounts(config.apiBase, accessToken as string, code, {
                page: accountsPage,
                pageSize: accountsPageSize,
                search: accountsSearch,
                outcome: accountsOutcomeFilter ?? undefined,
                organizationId: accountsOrgFilter ?? undefined,
                roleKey: accountsRoleFilter ?? undefined,
                ordering: accountsOrdering,
            }),
        enabled: enabled && Boolean(code),
        retry: false,
    });

    const accountRows: AppDetailAccountRow[] = accountsQuery.data?.results ?? [];
    const accountsTotal = accountsQuery.data?.count ?? 0;
    const accountsTotalPages = accountsQuery.data?.total_pages ?? 0;
    const accountsFacets = accountsQuery.data?.facets ?? null;
    const accountsLoading = enabled && accountsQuery.isLoading;
    const accountsError = accountsQuery.isError;
    const accountNeedsAttention = accountsFacets?.needs_attention ?? 0;

    // EAA-1817: AdminAudit has no `target_id` query filter server-side (only
    // action/target_type/success — see SRS-Portal-API-Apps-And-Roles.md §4.7
    // gap notes), so app-scoping happens client-side, same pattern
    // AccountAuditPanel's `lockedUsername` already uses for the identity table.
    // `target_type` is NOT filtered server-side either: the events that
    // belong to this app split across TWO target_types —
    // `application` (registry_upsert/app_ci_token_rotate/app_client_provision,
    // `target_id = code`) and `app_role` (app_role_create,
    // `target_id = "{code}:{roleKey}"`) — confirmed against
    // `console_views.py`. Filtering by `target_type` here would silently drop
    // one of the two, so this fetches the unfiltered page and does the whole
    // match client-side.
    const auditQuery = useQuery({
        queryKey: ['admin-audit', code, accessToken],
        queryFn: () => fetchAdminAudit(config.apiBase, accessToken as string, { page_size: 100 }),
        enabled: enabled && canViewAudit && Boolean(code),
        retry: false,
    });

    const auditRows = useMemo(
        () =>
            (auditQuery.data?.results ?? []).filter(
                (row) => row.target_id === code || row.target_id.startsWith(`${code}:`)
            ),
        [auditQuery.data, code]
    );

    return {
        code,
        canView,
        canManage,
        canViewAudit,
        canSync,
        canSyncUsers,
        canSyncOrganizations,
        tab,
        setTab,
        appsQuery,
        app,
        appRolesQuery,
        roleConversions,
        convertedCount,
        unconvertedCount,
        accountsLoading,
        accountsError,
        accountRows,
        accountNeedsAttention,
        accountsTotal,
        accountsTotalPages,
        accountsPage,
        setAccountsPage,
        accountsPageSize,
        setAccountsPageSize,
        accountsSearch,
        setAccountsSearch,
        accountsOutcomeFilter,
        setAccountsOutcomeFilter,
        accountsOrgFilter,
        setAccountsOrgFilter,
        accountsRoleFilter,
        setAccountsRoleFilter,
        accountsOrdering,
        setAccountsOrdering,
        accountsFacets,
        auditQuery,
        auditRows,
        navigateToRoleMap: () => navigate(`/console/role-map?application=${code}`),
    };
}

export type AppDetailPageVM = ReturnType<typeof useAppDetailPage>;
